/**
 * Cruise Data Utilities
 * Shared functions for handling cruise data across components
 */

const CruiseDataUtils = {
  /**
   * Detect which cruise line's product page we're on based on URL
   * @returns {string|null} The cruise line name or null if not on a partnership page
   */
  detectCruiseLine: function() {
    const currentUrl = window.location.pathname;

    // Map product handles to cruise line names
    const cruiseLineMapping = {
      '/products/cunard': 'Cunard',
      '/products/ambassador-cruise-line': 'Ambassador Cruise Line',
      '/products/fred-olsen-cruise-lines': 'Fred Olsen Cruise Lines',
      '/products/hurtigruten-expeditions': 'Hurtigruten Expeditions',
      '/products/marella-cruises': 'Marella Cruises',
      '/products/po-cruises': 'P&O Cruises', 
      '/products/semester-at-sea': 'Semester at Sea'
      // Add more mappings as needed
    };
    
    // Check if current URL matches any partnership product
    for (const urlPattern in cruiseLineMapping) {
      if (currentUrl.includes(urlPattern)) {
        return cruiseLineMapping[urlPattern];
      }
    }

    return null; // Not a partnership product
  },

  /**
   * Get cached data with expiration check
   * @param {string} key - The cache key
   * @param {number} expirationHours - Hours until cache expires (default: 24)
   * @returns {Object|null} The cached data or null if expired/not found
   */
  getCachedData: function(key, expirationHours = 24) {
    try {
      const cachedItem = localStorage.getItem(key);
      const timestampKey = `${key}_timestamp`;
      const timestamp = localStorage.getItem(timestampKey);

      if (!cachedItem || !timestamp) {
        return null;
      }

      const currentTime = Date.now();
      const expirationTime = parseInt(timestamp) + (expirationHours * 60 * 60 * 1000);
      if (currentTime > expirationTime) {
        // Cache expired, clean it up
        localStorage.removeItem(key);
        localStorage.removeItem(timestampKey);
        return null;
      }

      return JSON.parse(cachedItem);
    } catch (error) {
      console.warn('Error reading from cache:', error);
      return null;
    }
  },

  /**
   * Save data to cache with timestamp
   * @param {string} key - The cache key
   * @param {Object} data - The data to cache
   */
  setCachedData: function(key, data) {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      localStorage.setItem(`${key}_timestamp`, Date.now().toString());
    } catch (error) {
      console.warn('Error saving to cache:', error);
    }
  },

  /**
   * Handle API errors with fallback to expired cache
   * @param {string} key - The cache key
   * @param {Error} error - The error that occurred
   * @returns {Promise} A promise that resolves to the fallback data or rejects
   */
  handleApiError: function(key, error) {
    console.error(`API error for ${key}:`, error);

    // Try to get expired cache as fallback
    try {
      const cachedItem = localStorage.getItem(key);
      if (cachedItem) {
        return Promise.resolve(JSON.parse(cachedItem));
      }
    } catch (e) {
      console.error('Failed to use expired cache as fallback:', e);
    }

    return Promise.reject(error);
  },

  /**
   * Format ship display name
   * @param {Object} ship - Ship data from API
   * @returns {string} Formatted ship name
   */
  formatShipName: function(ship) {
    return `${ship.name} - ${ship.cruise_line}`;
  },

  /**
   * Format port display name
   * @param {Object} port - Port data from API
   * @returns {string} Formatted port name
   */
  formatPortName: function(port) {
    return `${port.name} - ${port.country}`;
  },

  // Store the port data fetching promise to ensure we only fetch once
  _portsFetchPromise: null,

  // Store the fetched ports for reuse
  _portsData: null,

  fallbackPorts: null,

  /**
   * Fetch fallback ports JSON from assets folder
   * @returns {Promise<Array>} Resolves to array of port objects
   */
  fetchFallbackPorts: function() {
    if (this.fallbackPorts) {
      return Promise.resolve(this.fallbackPorts);
    }
    return fetch(window.fallbackPortsUrl)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load fallback ports JSON');
        return res.json();
      })
      .then((data) => {
        this.fallbackPorts = data;
        return data;
      })
      .catch((err) => {
        console.error('Error loading fallback ports:', err);
        return [];
      });
  },

  /**
   * Shared function to fetch port data for multiple dropdowns
   * This ensures the data is only fetched once and can be reused
   * @returns {Promise} A promise that resolves to an array of port objects
   */
  fetchPorts: function() {
    console.log("CRUISE DATA: Starting shared ports fetch");

    // If we already have the ports data, return it immediately
    if (this._portsData) {
      console.log(`CRUISE DATA: Using already fetched ports data (${this._portsData.length} ports)`);
      return Promise.resolve(this._portsData);
    }

    // If a fetch is already in progress, return the existing promise
    if (this._portsFetchPromise) {
      console.log("CRUISE DATA: Port fetch already in progress, reusing the promise");
      return this._portsFetchPromise;
    }

    // First try to get data from cache
    const cachedPorts = this.getCachedData('cruise_ports_data');
    if (cachedPorts) {
      console.log(`CRUISE DATA: Using cached port data (${cachedPorts.length} ports)`);
      this._portsData = cachedPorts;
      return Promise.resolve(cachedPorts);
    }

    // If not in cache, fetch from API
    const myHeaders = new Headers();
    myHeaders.append("Accept", "application/json");
    myHeaders.append("x-api-key", "wb4u0f51-xc91-3824-8h7g-bv0s24j05gr8");
    myHeaders.append("x-client-id", "k3t2cx8p-5tqa-7d2l");
    myHeaders.append("Origin", "https://thecruisemaps.com");
    myHeaders.append("Referer", "https://thecruisemaps.com/");

    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow",
    };

    // Create a promise to fetch all port data
    this._portsFetchPromise = new Promise((resolve, reject) => {
      console.log("CRUISE DATA: Fetching ports from API");
      let allPorts = [];
      const perPage = 1000;

      // Recursive function to fetch paged data
      const fetchPortsPage = (page) => {
        console.log(`CRUISE DATA: Fetching ports page ${page+1}`);
        
        // Calculate proper skip value based on page number
        const skip = page * perPage;
        fetch(`https://nauticalmapper.com/api/v1/ports?filter_for_globe=true&sorting_for_globe=true&skip=${skip}&limit=${perPage}`, requestOptions)
          .then(response => {
            if (!response.ok) {
              throw new Error(`API responded with status: ${response.status}`);
            }
            return response.json();
          })
          .then(data => {
            console.log(`CRUISE DATA: Successfully fetched ports page ${page+1} (total in response: ${data.ports?.length || 0})`);
            
            // Log sample of data to verify format
            if (data.ports && Array.isArray(data.ports) && data.ports.length > 0) {
              console.log("CRUISE DATA: Sample port data:", data.ports[0]);
            }

            // Process ports
            if (data.ports && Array.isArray(data.ports)) {
              const newPorts = data.ports.map(port => ({
                name: port.name,
                country: port.country || '',
                display_name: `${port.name} - ${port.country || ''}`
              }));

              // Add to our collection
              allPorts = allPorts.concat(newPorts);
              console.log(`CRUISE DATA: Total ports processed so far: ${allPorts.length} of ${data.total_port_count}`);
              console.log(`CRUISE DATA: API response contained ${data.ports.length} ports, current page: ${page+1}`);
              
              // If we have more ports to fetch, get the next page
              if (allPorts.length < data.total_port_count && data.ports.length > 0) {
                console.log(`CRUISE DATA: Need to fetch more ports, proceeding to page ${page+2}`);
                fetchPortsPage(page + 1);
              } else {
                console.log(`CRUISE DATA: All ports fetched, total: ${allPorts.length}`);

                // Log ports by country for debugging
                const countryCounts = {};
                allPorts.forEach(port => {
                  if (!countryCounts[port.country]) {
                    countryCounts[port.country] = 0;
                  }
                  countryCounts[port.country]++;
                });
                console.log("CRUISE DATA: Ports by country:", countryCounts);

                // Sort ports alphabetically by name for better usability
                allPorts.sort((a, b) => a.name.localeCompare(b.name));

                // Cache the ports data
                this.setCachedData('cruise_ports_data', allPorts);
                console.log("CRUISE DATA: Port data cached successfully");

                // Store the result for future use
                this._portsData = allPorts;

                // Resolve the promise with the ports data
                resolve(allPorts);
              }
            } else {
              console.warn("CRUISE DATA: Unexpected API response format");
              throw new Error("Unexpected port data format");
            }
          })
          .catch(error => {
            console.error(`CRUISE DATA: Error fetching ports page ${page+1}:`, error);
            
            if (allPorts.length > 0) {
              console.log(`CRUISE DATA: Using partial data (${allPorts.length} ports) due to error`);

              // Sort what we have
              allPorts.sort((a, b) => a.name.localeCompare(b.name));

              // Cache partial data
              this.setCachedData('cruise_ports_data', allPorts);
              console.log(`CRUISE DATA: Partial port data (${allPorts.length} ports) cached successfully`);

              // Store the result for future use
              this._portsData = allPorts;

              // Resolve with partial data
              resolve(allPorts);
            } else {
              console.log("CRUISE DATA: Using fallback port data due to error");

              // Store the result for future use
              this.fetchFallbackPorts().then((fallbackData) => {
                this._portsData = fallbackData;
                resolve(fallbackData);
              });
            }
          });
      };

      // Start fetching from the first page
      fetchPortsPage(0);
    });
    
    return this._portsFetchPromise;
  }
};

// Make utilities available globally
window.CruiseDataUtils = CruiseDataUtils; 